import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
// Компоненты пользовательского интерфейса
import '../../styles/Infocards.css';
import Modal from '../Modal/Modal';
import FilterForInfocards from './FilterForInfocards';
import HeaderForObject from '../CustomElements/HeaderForObject';
import Table from '../Table/Table';
import CustomButton from '../CustomElements/CustomButton';
import InfocardCreationOrEditing from './InfocardCreationOrEditing';
import InfocardView from './InfocardView';
import DownloadReferenceForm from './DownloadReferenceForm';
import { useLoader } from './Common';
import translations from '../../content/PagesContent/InfocardPages/infocardPageTranslations.json';
// Кастомные хуки
import useUrlNavigate from '../../customHook/useUrlNavigate';
import { useRole } from '../../customHook/useRole';
import { useFilterControl } from '../../customHook/useFilterControl';
import useInfiniteScroll from '../../customHook/useInfiniteScroll';
import useTranslations from '../../customHook/useTranslations';
// Утилиты
import { defaultCellRender } from '../../utils/formatValueForHTML';
// Действия Redux
import { clearInfocards, initInfocardsFromServer } from '../../redux/actions/infocards.action';
import { remove } from '../../redux/actions/loader.action';
import { updateSortData } from '../../redux/actions/sortData.action';

// компонент для отображения страницы инфокарты

function Infocards(props) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();

  // получение словаря для статических данных в зависимости от выбранного языка
  const t = useTranslations(translations);

  // роли пользователя
  const isAdmin = useRole('admin');

  // хук для отслеживания процесса запросов
  const { isLoading, isSuccess, isFailed } = useLoader();

  //получаем все инфокарты из стейта
  const infocards = useSelector((state) => state.infocards.items);

  //стейт для инпута поиска
  const [searchInput, setSearchInput] = useState('');

  //хук для навигации
  const changeURL = useUrlNavigate();

  // Начальное состояние для стейта фильтрации передаем пропсами
  const initialFormData = {
    road: '',
    directorate: '',
    department: '',
    station: '',
    version: '',
    isDisabled: '',
  };

  const [formData, setFormData] = useState(initialFormData);

  // хук для сброса фильтров и флага для отображения кнопки сброса
  const { resetFilters, showBtnReset } = useFilterControl(initialFormData, formData, setFormData, '', setSearchInput);

  // хук для запроса данных с учетом параметров поиска фильтрации и сортировки, и контроля скрола
  const { bottomBoundaryRef, handleSearch } = useInfiniteScroll(
    formData, // Данные фильтров
    searchInput, // Строка поиска
    setSearchInput, // Функция для изменения строки поиска
    initInfocardsFromServer, // Функция для инициализации данных
    'infocards', // Redux-селектор
    resetFilters, // Сброс фильтров
    clearInfocards, // Очистка стейта
    false // Флаг для остановки запросов
  );

  //открытие модального окна для создания инфокарты
  const navigateToCreateInfocard = () => {
    changeURL((u, rootpath) => {
      u.pathname = `${rootpath}/create`;
    });
  };

  //открытие модального окна для создания инфокарты
  const navigateToDownloadReference = () => {
    changeURL((u, rootpath) => {
      u.pathname = `${rootpath}/reference`;
    });
  };

  //закрытие модального окна подтверждения редактирования инфокарты
  const closeSuccessEditModal = () => {
    dispatch(remove());
    changeURL((u) => {
      u.pathname = u.pathname.replace('edit/', 'view/');
    });
  };

  // функция для перехода к инфокарте из таблицы
  const openInfocard = (_, { stationId }) => {
    if (!stationId) return;

    changeURL((url, basePath) => {
      url.pathname = `${basePath}/view/${stationId}`;
    });
  };

  // функция для закрытия модальных окон и навигации
  const closeModalHandler = useCallback(
    (toRoot = false) => {
      // Блокируем закрытие окна во время загрузки
      if (isLoading) return;

      // Убираем состояние загрузки, если операция успешна
      if (isSuccess) {
        dispatch(remove());
      }

      changeURL((url, rootPath) => {
        url.pathname = toRoot || isFailed ? rootPath : url.pathname.replace('edit/', 'view/');
      });
    },
    [changeURL, isLoading, isSuccess, isFailed, dispatch]
  );
  const closeModalToRoot = useCallback(() => closeModalHandler(true), [closeModalHandler]);

  const columns = useMemo(
    () => [
      {
        Header: t?.road || 'Дорога',
        accessor: 'roadName',
        Cell: ({ value }) => (
          <div className="road-row">
            <div className="road-icon"></div>
            <div className="road-text">
              <span>{value}</span>
            </div>
          </div>
        ),
      },
      {
        Header: t?.department || 'Подразделение',
        accessor: 'departmentName',
        Cell: ({ value }) => <span className="text-nowrap">{defaultCellRender(value)}</span>,
      },
      // {
      //   Header: 'Регион',
      //   accessor: 'region',
      //   Cell: ({ value }) => <span className="text-nowrap">{defaultCellRender(value)}</span>,
      // },
      {
        Header: t?.stationOrSegment || 'Станция/Перегон',
        accessor: 'stationName',
        Cell: ({ value }) => <span className="text-break">{defaultCellRender(value)}</span>,
      },
      {
        Header: t?.type || 'Тип',
        accessor: 'isStation',
        Cell: ({ value }) => (
          <span className="text-nowrap">{value ? t?.station || 'Станция' : t?.segment || 'Перегон'}</span>
        ),
      },
      {
        Header: t?.direction || 'Направление',
        accessor: 'direction',
        Cell: ({ value }) => <span className="text-break">{defaultCellRender(value)}</span>,
      },
      {
        Header: t?.version || 'Версия',
        accessor: 'version',
        Cell: ({ value }) => <span className="text-break">{defaultCellRender(value)}</span>,
      },
      {
        Header: t?.versionKit || 'Версия КИТ',
        accessor: 'versionKit',
        disableSorting: true, // Флаг для отключения сортировки
        tooltipText: t?.noSorting || 'Сортировка не активна',
        Cell: ({ value }) => <span className="text-break">{defaultCellRender(value)}</span>,
      },
      {
        Header: t?.enabled || 'Вкл',
        accessor: 'isDisabled',
        Cell: ({ value }) => <span className="text-break">{!value ? t?.yes || 'Да' : t?.no || 'Нет'}</span>,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [infocards, t]
  );

  // при закрытии компонента очищаем стейт инфокарт и сбрасываем  сортировки
  useEffect(() => {
    return () => {
      dispatch(clearInfocards());
      dispatch(updateSortData({}));
    };
  }, [dispatch]);

  return (
    <div className="infocard">
      <FilterForInfocards
        initialFormData={initialFormData}
        formData={formData}
        setFormData={setFormData}
        showBtnReset={showBtnReset}
        resetFilters={resetFilters}
        t={t}
      />
      <div id="infocard-container" className="d-flex flex-column flex-nowrap table-container w-100">
        <HeaderForObject
          searchInput={searchInput}
          onFilterChange={(e) => {
            setSearchInput(e?.target?.value);
          }}
          onFilterSubmit={handleSearch}
        />
        <Table
          data={infocards}
          columns={columns}
          open={openInfocard}
          showGlobalSearch={false}
          bottomBoundaryRef={bottomBoundaryRef}
          arrLength={infocards?.length}
          disableInternalSorting
        />

        <div className={`infocards_btn-container ${!isAdmin ? 'padding-10' : ''}`}>
          {isAdmin && (
            <CustomButton
              text={t?.createInfocard || '+ Создать информационную карту'}
              onClick={navigateToCreateInfocard}
            />
          )}
          {isAdmin && (
            <CustomButton text={t?.uploadDirectory || '+ Загрузить справочник'} onClick={navigateToDownloadReference} />
          )}
        </div>
      </div>

      <Routes>
        <Route
          path="/edit/:stationId/*"
          element={
            <Modal isOpen onClose={closeSuccessEditModal} containerSelector="#infocard-container">
              <InfocardCreationOrEditing closeModal={closeSuccessEditModal} t={t} />
            </Modal>
          }
        />
        <Route
          path="/create/*"
          element={
            <Modal isOpen onClose={closeModalToRoot} containerSelector="#infocard-container">
              <InfocardCreationOrEditing closeModal={closeModalToRoot} t={t} />
            </Modal>
          }
        />
        <Route
          path="/view/:stationId/*"
          element={
            <Modal isOpen onClose={closeModalToRoot} containerSelector=".infocard" minusSize="--sadr-font-size-40">
              <InfocardView closeModal={closeModalToRoot} t={t} />
            </Modal>
          }
        />

        <Route
          path="/reference"
          element={
            <Modal containerSelector="#infocard-container" isOpen onClose={closeModalToRoot}>
              <DownloadReferenceForm closeModal={closeModalToRoot} formData={formData} t={t} />
            </Modal>
          }
        />
      </Routes>
    </div>
  );
}

export default Infocards;
